import { Map, View } from 'ol';
// import TileLayer from 'ol/layer/Tile';
// import OSM from 'ol/source/OSM';
import { defaults as defaultControls } from 'ol/control';
import ZoomSlider from 'ol/control/ZoomSlider';
import { extend, intersects } from 'ol/extent';

import { newSwitch, WizardSwitch } from './controls';
import { rightSize } from './draw';
import { getFeatureBy } from './feature-tools';
import { getWMS } from './layers';
import { idb, rememberLocation, restoreLocation } from './storage';

export const map = new Map({
  target: 'map',
  // layers: [ ], // new TileLayer({ source: new OSM() }) ],
  view: restoreLocation(),
  controls: defaultControls().extend([
    new ZoomSlider, new WizardSwitch,
    newSwitch('flybilder', 'Nyeste', 'flyFS'),
    newSwitch('ar5', 'AR5', 'gkFS'),
    newSwitch('grunnkart', 'Grunnkart', 'gkFS'),
    newSwitch('skogbonitet', 'Skogbonitet', 'gkFS'),
    newSwitch('kp', 'Nåværende', 'kFS'),
    newSwitch('kpp', 'Planområde nåværende', 'kFS'),
    newSwitch('kpf', 'Forslag', 'kFS'),
    newSwitch('kpfp', 'Planområde forslag', 'kFS'),
    newSwitch('kdp', 'Nåværende', 'kdFS'),
    newSwitch('kdpp', 'Planområde nåværende', 'kdFS'),
    newSwitch('kdpf', 'Forslag', 'kdFS'),
    newSwitch('kdpfp', 'Planområde forslag', 'kdFS'),
    newSwitch('rp', 'Nåværende', 'rFS'),
    newSwitch('rpp', 'Planområde nåværende', 'rFS'),
    newSwitch('rpf', 'Forslag', 'rFS'),
    newSwitch('rpfp', 'Planområde forslag', 'rFS'),
    newSwitch('pip', 'Planområde', 'piFS'),
    newSwitch('eiendom', 'Grenser', 'egFS'),
    newSwitch('gnr', 'Gårds- og bruksnummer', 'egFS'),
    // newSwitch('tønsberg', 'Tønsberg og Drammen', 'utFS'),
  ])
});

map.on('moveend', () => rememberLocation(map.getView()));

export const getLayerByFeature = (f) => {
  const layers = map.getLayers();
  const nl = layers.getLength();
  for (let i = 0; i < nl; i++) {
    const l = layers.item(i);
    const drawn = (l.get('type') === 'drawings');
    const uploaded = (l.get('type') === 'file');
    if ((drawn || uploaded) && l.getSource().hasFeature(f)) return l;
  }
  return null;
};

export const getLayerByPlanId = (planid) => {
  const layers = map.getLayers();
  const nl = layers.getLength();
  for (let i = 0; i < nl; i++) {
    const l = layers.item(i);
    const drawn = (l.get('type') === 'drawings');
    const uploaded = (l.get('type') === 'file');
    if (
      (drawn || uploaded) &&
      (getFeatureBy('planid', planid, l) || getFeatureBy('title', planid, l))
    ) return l;
  }
  return null;
};

export const getLegendUrl = (wmsId, layerName) => {
  const nwu = getWMS(wmsId).getSource().getLegendUrl(undefined, layerName);
  const v1 = nwu.replace('1.3.0', '1.1.1');
  const wu = v1.split('LAYER=')[0]
  // return v1.split('%2C')[0];
  return wu + 'LAYER=' + layerName;
};

const zoomToExtent = e => {
  const view = map.getView();
  const z = view.getZoom();
  const fit = () => view.fit(e, { duration: 2000, padding: [50, 50, 50, 50] });
  const visibleDestination = intersects(view.calculateExtent(), e);
  if (z < 8 || visibleDestination) fit();
  else view.animate({ zoom: 7 }, fit);
};

export const zoomToFeature = (feature) => {
  // map.getView().fit(feature.getGeometry());
  zoomToExtent(feature.getGeometry().getExtent());
};

export const zoomToLayer = (layer) => {
  zoomToExtent(layer.getSource().getExtent());
};

export const zoomToAll = (layers) => {
  let all;
  layers.forEach(layer => {
    const some = layer.getSource().getFeatures().length > 0;
    const single = some ? layer.getSource().getExtent() : all;
    if (!all) all = single; else extend(all, single);
  });
  if (all) zoomToExtent(all);
};

export const zoomToPlanId = (planid, layer) => {
  let all;
  layer.getSource().getFeatures().forEach(f => {
    if (f.get('planid') === planid || f.get('title') === planid){
      const single = f.getGeometry().getExtent();
      if (!all) all = single; else extend(all, single);
    }
  });
  if (all) zoomToExtent(all);
};

export const togglePlanId = (layer, planid, active) => {
  layer.getSource().getFeatures().forEach(feature => {
    const match = (feature.get('suitable') && feature.get('planid') === planid);
    if (match) feature.set('checked', active);
  });
  if (layer.get('id') === 'drawings') idb.updateLayer(layer);
};

export const highlightPlanId = (planid, highlight, layer) => {
  if (!layer) layer = getLayerByPlanId(planid);
  layer.getSource().getFeatures().forEach(f => {
    if (f.get('planid') === planid || f.get('title') === planid)
      f.set('highlight', highlight);
  });
};

export const setPlanIdOpacity = (layer, planid, opacity) => {
  layer.getSource().forEachFeature(feature => {
    if (feature.get('planid') === planid) feature.set('opacity', opacity);
  });
};

export const setPlanIdTitle = (layer, planid, title) => {
  layer.getSource().forEachFeature(feature => {
    if (feature.get('planid') === planid) {
      feature.set('title', title);
      if (feature.get('drawn')) feature.set('feltnavn', title);
    }
  });
  if (layer.get('id') === 'drawings') idb.updateLayer(layer);
};

export const removePlanId = (layer, planid) => {
  const s = layer.getSource();
  s.forEachFeature(feature => {
    if (feature.get('planid') === planid) s.removeFeature(feature);
  });
  if (layer.get('id') === 'drawings') idb.updateLayer(layer);
};

const extra = [
  'checked',
  'drawn',
  'feltnavn',
  'formål',
  'highlight',
  'id',
  'NASJONALAREALPLANID',
  'objekttypenavn',
  'opacity',
  // 'plankategori',
  'suitable',
  // 'title'
];
const removeExtra = f => {
  extra.forEach(e => f.set(e, undefined));
  return f;
};

export const getListSelectedFeatures = () => {
  const filteredFeatures = [];
  // Send features belonging to drawn and uploaded layers of right size:
  map.getLayers().getArray().filter(
    l => ['drawings', 'file'].indexOf(l.get('type')) > -1
  ).forEach(s => s.getSource().getFeatures().filter(f =>
    f.get('checked') && (
      f.get('arealformål') ||
      (['Fremtidig', 'Nåværende'].indexOf(f.get('objtype')) > -1)
    ) && rightSize(f)
  ).forEach(f => filteredFeatures.push(removeExtra(f.clone()))));
  return filteredFeatures;
};
